/*
 * RIPOST — animated free-shipping bar (Lottie).
 * Enhances the theme's cart progress bar (blocks/_cart-progress-bar.liquid) only.
 * - progress / unlocked come from the Liquid block (same values as the original <progress>)
 * - the cart drawer re-renders its markup on every `cart:updated`; the player + canvas are
 *   kept per bar (drawer / cart page) and re-adopted by the new element, so the bike glides
 *   and the confetti plays when the cart crosses the threshold, with no reload.
 * - if the player fails, the original <progress> bar stays (no reserved space).
 * - prefers-reduced-motion: static frame, no state machine, no confetti.
 */
const PLAYER_VERSION = '0.81.0';
const PLAYER_URL = `https://cdn.jsdelivr.net/npm/@lottiefiles/dotlottie-web@${PLAYER_VERSION}/dist/index.js`;
const WASM_URL = `https://cdn.jsdelivr.net/npm/@lottiefiles/dotlottie-web@${PLAYER_VERSION}/dist/dotlottie-player.wasm`;
const X0 = 20, X1 = 322, Y = 81, TRACK = 330;
const LOAD_TIMEOUT = 10000;
const reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

let playerPromise = null;
function loadPlayer() {
  if (!playerPromise) {
    playerPromise = import(PLAYER_URL).then((m) => {
      m.DotLottie.setWasmUrl(WASM_URL);
      return m.DotLottie;
    });
  }
  return playerPromise;
}

const instances = new Map(); // key -> { canvas, anim, sm, p, unlocked, ready, failed }

const mem = {
  get(key) { try { return JSON.parse(sessionStorage.getItem('ripost-ship-bar:' + key)); } catch (e) { return null; } },
  set(key, v) { try { sessionStorage.setItem('ripost-ship-bar:' + key, JSON.stringify(v)); } catch (e) { /* private mode */ } },
};

function toRGBA(css, fallback) {
  const c = document.createElement('canvas').getContext('2d');
  c.fillStyle = '#000';
  c.fillStyle = css || fallback;
  c.fillRect(0, 0, 1, 1);
  const d = c.getImageData(0, 0, 1, 1).data;
  return [d[0] / 255, d[1] / 255, d[2] / 255, 1];
}
const luminance = ([r, g, b]) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

class RipostShipBar extends HTMLElement {
  connectedCallback() {
    this.key = this.dataset.key || 'cart';
    this.p = Math.max(0, Math.min(1, parseFloat(this.dataset.progress) / 100 || 0));
    this.unlocked = this.dataset.unlocked === 'true';
    this.group = this.closest('.cart-progress-bar__progress-group');

    const prev = mem.get(this.key);
    mem.set(this.key, { p: this.p, unlocked: this.unlocked });

    const inst = instances.get(this.key);
    if (inst && inst.failed) return this.#fail();
    this.classList.add('is-pending');            // reserve the space: no jump when the bike appears
    this.group?.classList.add('ripost-ship-bar--on');

    if (inst && inst.ready) {                     // drawer re-render: adopt the running player
      this.querySelector('canvas')?.replaceWith(inst.canvas);
      this.#markReady();
      this.#update(inst, false);
      return;
    }
    if (inst) {                                    // still loading: hand over the canvas, update on load
      this.querySelector('canvas')?.replaceWith(inst.canvas);
      inst.owner = this;
      return;
    }
    this.prev = prev;
    // lazy: start the player when the bar is first visible (drawer opened / cart page)
    this.io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { this.io.disconnect(); this.#init(); }
    });
    this.io.observe(this);
  }

  disconnectedCallback() { this.io?.disconnect(); }

  async #init() {
    const canvas = this.querySelector('canvas');
    const inst = { canvas, anim: null, sm: false, p: this.prev ? this.prev.p : this.p, unlocked: this.prev ? this.prev.unlocked : this.unlocked, ready: false, failed: false, owner: this };
    instances.set(this.key, inst);
    const timer = setTimeout(() => this.#failInst(inst, 'timeout'), LOAD_TIMEOUT);
    try {
      const DotLottie = await loadPlayer();
      const reduce = reduceMQ.matches;
      const anim = new DotLottie({ canvas, src: this.dataset.src, autoplay: false, loop: false, renderConfig: { autoResize: true } });
      inst.anim = anim;
      anim.addEventListener('loadError', () => this.#failInst(inst, 'loadError'));
      anim.addEventListener('load', () => {
        clearTimeout(timer);
        if (inst.failed) return;
        const owner = inst.owner;
        this.#applyColors(anim, owner);
        if (!reduce && anim.stateMachineLoad('shipBar') && anim.stateMachineStart()) {
          inst.sm = true;
          // goal already met when the bar first shows (and not just crossed): calm loop, no confetti
          if (inst.unlocked && owner.unlocked) {
            anim.stateMachineSetBooleanInput('unlocked', true);
            anim.stateMachineSetNumericInput('progress', 100);
            anim.stateMachineOverrideState('Unlocked');
          }
        }
        inst.reduce = reduce;
        this.#setSlots(inst, inst.p);
        inst.ready = true;
        owner.#markReady();
        owner.#update(inst, true);
      });
    } catch (e) {
      clearTimeout(timer);
      this.#failInst(inst, e);
    }
  }

  #applyColors(anim, el) {
    // follow the block's colour scheme: line-art = text colour, fill = theme bar colour,
    // accent (pennant, confetti) = RIPOST green on light backgrounds, text colour on dark/green ones
    const cs = getComputedStyle(el.closest('.cart-progress-bar') || el);
    const text = toRGBA(cs.color, '#2a2a2a');
    const fill = toRGBA(cs.getPropertyValue('--color-primary-button-background').trim(), '#2a2a2a');
    const darkBg = luminance(text) > 0.6;
    anim.setColorSlot('c_grey', text);
    anim.setColorSlot('c_fill', fill);
    anim.setColorSlot('c_green', darkBg ? text : [5 / 255, 97 / 255, 75 / 255, 1]);
  }

  #setSlots(inst, p) {
    inst.anim.setVectorSlot('bike_pos', [X0 + (X1 - X0) * p, Y]);
    inst.anim.setVectorSlot('fill_pos', [p <= 0 ? -TRACK - 10 : -TRACK * (1 - p), 0]);
    if (!inst.anim.isPlaying) inst.anim.setFrame(inst.anim.currentFrame || 0);
  }

  #update(inst, first) {
    const anim = inst.anim, p = this.p, unlocked = this.unlocked;
    // glide the bike + fill (400 ms ease-out)
    cancelAnimationFrame(inst.raf);
    if (inst.reduce) {
      inst.p = p; this.#setSlots(inst, p);
      anim.setFrame(unlocked ? 100 : 0);
    } else {
      const from = inst.p, t0 = performance.now();
      const step = (now) => {
        const k = Math.min(1, (now - t0) / 400), e = 1 - Math.pow(1 - k, 3);
        inst.p = from + (p - from) * e; this.#setSlots(inst, inst.p);
        if (k < 1) inst.raf = requestAnimationFrame(step);
      };
      inst.raf = requestAnimationFrame(step);
    }
    if (inst.sm) {
      const pct = Math.round(p * 100);
      // guards run on each input change: set the "leading" input first
      if (unlocked) { anim.stateMachineSetBooleanInput('unlocked', true); anim.stateMachineSetNumericInput('progress', pct); }
      else { anim.stateMachineSetNumericInput('progress', pct); anim.stateMachineSetBooleanInput('unlocked', false); }
    }
    inst.unlocked = unlocked;
    window.dispatchEvent(new CustomEvent('ripost-ship-bar:update', { detail: { key: this.key, p, unlocked, first } }));
  }

  #markReady() { this.classList.remove('is-pending'); this.classList.add('is-ready'); this.group?.classList.add('ripost-ship-bar--on', 'ripost-ship-bar--ready'); }

  #failInst(inst, err) {
    if (inst.failed || inst.ready) return;
    inst.failed = true;
    console.warn('[ripost-ship-bar] animation unavailable, keeping the default bar', err);
    try { inst.anim?.destroy(); } catch (e) { /* ignore */ }
    inst.owner.#fail();
  }

  #fail() { this.classList.remove('is-pending', 'is-ready'); this.classList.add('is-failed'); this.group?.classList.remove('ripost-ship-bar--on', 'ripost-ship-bar--ready'); }
}

if (!customElements.get('ripost-ship-bar')) customElements.define('ripost-ship-bar', RipostShipBar);
window.RipostShipBar = { instances };
